import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Save, Shield, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { companiesClient, requestsClient } from '../auth/keycloakProvider';
import { RegisterCompanyEmployeeCommand, SignAndSubmitBlockchainRequestCommand } from '../web-api-client';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';
import { Separator } from '../components/ui/separator';
import { RestrictionLevel } from '../types';
import { getEmployee, editEmployee } from '../api-clients';

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    role: 'CompanyDriver' as 'CompanyDriver' | 'CompanyManager' | 'CompanyDispatcher' | 'CompanyAdministrator',
    restrictionLevel: 0 as RestrictionLevel,
    username: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing || !id) return;

    const loadEmployee = async () => {
      setIsLoadingData(true);
      try {
        const employeeId = parseInt(id, 10);
        if (isNaN(employeeId)) {
          throw new Error('ID inválido');
        }
        const employee = await getEmployee(employeeId);
        setFormData({
          fullName: employee.fullName || '',
          email: employee.email || '',
          phoneNumber: employee.phoneNumber || '',
          role: (employee.role as typeof formData.role) || 'CompanyDriver',
          restrictionLevel: (employee.restrictionLevel ?? 0) as RestrictionLevel,
          username: '',
        });
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Erro ao carregar dados do colaborador');
        setError(errorToast.description);
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    loadEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isEditing) {
        const employeeId = parseInt(id!, 10);
        const response = await editEmployee({
          employeeId,
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber || undefined,
          restrictionLevel: formData.restrictionLevel ?? 0,
        });

        if (response.requestUUID) {
          await requestsClient.signAndSubmitBlockchainRequest(
            new SignAndSubmitBlockchainRequestCommand({
              requestUUID: response.requestUUID,
            })
          );
        }

        toast({
          title: 'Sucesso',
          description: `Colaborador "${formData.fullName}" atualizado com sucesso!`,
        });

        setTimeout(() => {
          navigate(`/employees/${id}`);
        }, 1500);
      } else {
        const command = new RegisterCompanyEmployeeCommand({
          fullName: formData.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber || undefined,
          restrictionLevel: formData.restrictionLevel ?? 0,
          role: formData.role,
          username: formData.username,
        });

        await companiesClient.registerCompanyEmployee(command);

        toast({
          title: 'Sucesso',
          description: `Colaborador "${formData.fullName}" registado com sucesso!`,
        });

        setTimeout(() => {
          navigate('/employees');
        }, 1500);
      }
    } catch (err) {
      const context = isEditing ? 'atualizar' : 'registar';
      const errorToast = getErrorToast(err, `Erro ao ${context} colaborador`, `Falha ao ${context} colaborador`);
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Editar Colaborador' : 'Novo Colaborador'}
        description={isEditing ? 'Atualizar dados do colaborador' : 'Registar um novo colaborador no sistema'}
        backLink={isEditing ? `/employees/${id}` : '/employees'}
      />

      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informações do Colaborador
            </CardTitle>
            <CardDescription>
              {isEditing
                ? 'Atualize os dados do colaborador'
                : 'Preencha os dados do colaborador para registo no sistema'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Nome Completo *</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                placeholder="Ex: João Silva"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Ex: joao.silva@empresa.pt"
                required={!isEditing}
                disabled={isEditing}
              />
              {isEditing ? (
                <p className="text-xs text-muted-foreground">
                  O email não pode ser alterado
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  O colaborador receberá um email de boas-vindas com instruções de acesso
                </p>
              )}
            </div>
            {!isEditing && (
              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  required
                  disabled={isEditing}
                />
                {isEditing && (
                  <p className="text-xs text-muted-foreground">
                    O username não pode ser alterado
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Telefone</Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                placeholder="Ex: +351 91 234 5678"
              />
            </div>
            {!isEditing && (
              <div className="space-y-2">
                <Label htmlFor="role">Função *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(v) => handleInputChange('role', v as typeof formData.role)}
                >
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CompanyDriver">Motorista</SelectItem>
                    <SelectItem value="CompanyManager">Gestor</SelectItem>
                    <SelectItem value="CompanyDispatcher">Expedidor</SelectItem>
                    <SelectItem value="CompanyAdministrator">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {!isEditing && (
              <div className="pt-4 border-t mt-6">
                <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                  <p>• O colaborador será registado no sistema e na blockchain NEXUS</p>
                  <p>• No primeiro login, será solicitado que configure a wallet</p>
                  <p>• As permissões dependem da função selecionada</p>
                </div>
              </div>
            )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Restrições de Acesso
              </CardTitle>
              <CardDescription>
                Configure o nível de restrição de acesso deste colaborador
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="restrictionLevel">Nível de Restrição</Label>
                <Select
                  value={formData.restrictionLevel.toString()}
                  onValueChange={(v) => handleInputChange('restrictionLevel', parseInt(v) as RestrictionLevel)}
                >
                  <SelectTrigger id="restrictionLevel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nível 0 - Sem Restrição</SelectItem>
                    <SelectItem value="1">Nível 1 - Restrição Parcial</SelectItem>
                    <SelectItem value="2">Nível 2 - Restrito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Separator />
              
              <div className="space-y-3 text-sm">
                <div className="p-3 rounded-lg bg-restriction-0-bg text-restriction-0-foreground">
                  <p className="font-medium">Nível 0 - Sem Restrição</p>
                  <p className="text-xs mt-1">Acesso completo a todas as funcionalidades permitidas pelo seu cargo</p>
                </div>
                <div className="p-3 rounded-lg bg-restriction-1-bg text-restriction-1-foreground">
                  <p className="font-medium">Nível 1 - Restrição Parcial</p>
                  <p className="text-xs mt-1">Pode visualizar mas não pode criar ou editar documentos</p>
                </div>
                <div className="p-3 rounded-lg bg-restriction-2-bg text-restriction-2-foreground">
                  <p className="font-medium">Nível 2 - Restrito</p>
                  <p className="text-xs mt-1">Acesso completamente bloqueado ao sistema</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate(isEditing ? `/employees/${id}` : '/employees')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isEditing ? 'A guardar...' : 'A registar...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Guardar Alterações' : 'Registar Colaborador'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
